import React, { useMemo } from 'react';
import { Table, TableBody, TableContainer } from '@mui/material';
import { useStyles } from './styles';
import CategoriesTableHead from '../CategoriesTableHead/CategoriesTableHead';
import { useCategoriesContext } from '../../../../context/CategoriesContext';
import CategoriesTableRow from '../CategoriesTableRow/CategoriesTableRow';
import { IColumn, IUserMenu } from '../../categoriesManagement.types';

const TABLE_COLUMNS: readonly IColumn[] = [
  { id: 'image', label: 'IMAGE', minWidth: 80, sortable: false },
  { id: 'foodName', label: 'FOOD_NAME', minWidth: 150, sortable: true },
  { id: 'foodTruck', label: 'FOOD_TRUCK', minWidth: 200, sortable: true },
  { id: 'price', label: 'PRICE', minWidth: 100, sortable: true },
  { id: 'category', label: 'CATEGORY', minWidth: 120, sortable: true },
];

const CategoriesTable: React.FC = () => {
  const classes = useStyles();
  const { foodMenu, selectedIds, order, orderBy, handleSelectAll, handleSelect, handleRequestSort } = useCategoriesContext();

  const categoriesData: IUserMenu[] = useMemo(() => {
    return foodMenu.foods.map((food: any) => {
      return {
        id: food.id,
        imageUrl: food.image_url || '',
        foodName: food.name,
        foodTruck: food.restaurantName || '',
        price: String(food.price),
        category: food.categoryNames || '',
        enabled: food.enabled,
      };
    });
  }, [foodMenu.foods]);

  const isSelected = (id: string) => selectedIds.indexOf(id) !== -1;

  const handleSelectAllChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleSelectAll(event.target.checked);
  };

  const handleSortRequest = (property: 'image' | 'foodName' | 'foodTruck' | 'price' | 'category') => {
    handleRequestSort(property);
  };

  const sortedData = useMemo(() => {
    if (!orderBy) return categoriesData;

    return [...categoriesData].sort((a, b) => {
      const aValue = a[orderBy as keyof IUserMenu];
      const bValue = b[orderBy as keyof IUserMenu];

      const aStr = String(aValue || '');
      const bStr = String(bValue || '');

      return order === 'asc'
        ? aStr.localeCompare(bStr, 'ja', {
          usage: 'sort',
          sensitivity: 'base',
          numeric: true,
        })
        : bStr.localeCompare(aStr, 'ja', {
          usage: 'sort',
          sensitivity: 'base',
          numeric: true,
        });
    });
  }, [categoriesData, order, orderBy]);

  return (
    <TableContainer className={classes.TableContainer}>
      <Table stickyHeader>
        <CategoriesTableHead
          columns={TABLE_COLUMNS}
          numSelected={selectedIds.length}
          rowCount={categoriesData.length}
          onSelectAllClick={handleSelectAllChange}
          order={order}
          orderBy={orderBy}
          onRequestSort={handleSortRequest}
        />
        <TableBody>
          {sortedData.map((item) => (
            <CategoriesTableRow key={item.id} item={item} isSelected={isSelected(item.id)} onSelect={handleSelect} />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default CategoriesTable;
